import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Table, Row, Button, Input, Pagination, PaginationItem, PaginationLink, FormGroup, Col, Label } from 'reactstrap';
import { useTranslate } from 'react-translate';
import { useSortBy, useTable, usePagination } from 'react-table';
import ScrollButton from 'components/Common/ScrollButton';
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';
import { actionGetAnalysisList } from 'store/actions/analysisActions';
import { actionSetAnalysisPageIndex } from 'store/actions/analysisActions';
import moment from 'moment';

const AnalysisTableList = () => {

    const t = useTranslate('analysis');
    const tTypes = useTranslate('study-type');
    const dispatch = useDispatch();
    const refreshTable = useRef(false);
    const isLoading = useSelector(state => state.analysisReducer.loadingAnalysisList);
    const lastPage = useSelector(state => state.analysisReducer.lastPage);
    const pageLimit = useSelector(state => state.analysisReducer.limit);
    const pageState = useSelector(state => state.analysisReducer.page);
    const totalEntries = useSelector(state => state.analysisReducer.totalEntries);
    const querySearch = useSelector(state => state.analysisReducer.querySearch);

    const practiceId = useSelector(state => state.practicesReducer.practice._id);
    const user = useSelector(state => state.userReducer);

    const [sortDate, setSortDate] = useState(-1);

    const analysisData = useSelector(state => state.analysisReducer.entries);

    const data = useMemo(
        () => analysisData.length ? analysisData.map((d,i) => {
            return {
                c1: tTypes(d.type.replace(' ', '-')),
                c2: d.analysis_id,
                c3: d.device_id,
                c4: moment(d.created_at).format(user.dateFormat),
                c5: `${d.doctor.firstName} ${d.doctor.lastName}`,
                c6: `${d.patient.firstName} ${d.patient.lastName}`
            }
        }) : [],
        //eslint-disable-next-line
        [analysisData, t]
    );

    const columns = useMemo(
        () => [
            {
                Header: t("study-type"),
                accessor: "c1",
                defaultCanSort: false,
                disableSortBy: true
            },
            {
                Header: `ID`,
                accessor: "c2",
                defaultCanSort: false,
                disableSortBy: true
            },
            {
                Header: t("device-id"),
                accessor: "c3",
                defaultCanSort: false,
                disableSortBy: true
            },
            {
                Header: t("start-date"),
                accessor: "c4",
                defaultCanSort: false,
                disableSortBy: true
            },
            {
                Header: t("doctor"),
                accessor: "c5",
                defaultCanSort: false,
                disableSortBy: true
            },
            {
                Header: t("patient"),
                accessor: "c6",
                defaultCanSort: false,
                disableSortBy: true
            },
        ],
        //eslint-disable-next-line
        [t]
    );

    const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            page,
            prepareRow,
            canPreviousPage,
            canNextPage,
            pageOptions,
            pageCount,
            gotoPage,
            nextPage,
            previousPage,
            setPageSize,
            state: { pageIndex, pageSize }
        } = useTable({
            columns,
            data,
            disableSortRemove: true,
            enableSortingRemoval: true,
            defaultCanSort: true,
            initialState: {
                pageSize: pageLimit,
                pageIndex: pageState,
                hiddenColumns: ["registerId", "additionalInfo"],
            },
            manualPagination: true,
            pageCount: lastPage + (totalEntries > pageLimit),
            autoResetPage: !refreshTable.current
        },
            useSortBy,
            usePagination);

    const goToPageNumber = (page) => {
        dispatch(actionSetAnalysisPageIndex(page));
        gotoPage(page);
    }

    const goNextPage = () => {
        dispatch(actionSetAnalysisPageIndex(pageIndex + 1));
        nextPage();
    }

    const goPreviousPage = () => {
        dispatch(actionSetAnalysisPageIndex(pageIndex - 1));
        previousPage();
    }

    useEffect(() => {
        refreshTable.current = true;
        document.getElementById("full-page").scroll(0,0);

        //Call to recieve the studies
        dispatch(actionGetAnalysisList({
            practiceId, 
            pageIndex, 
            pageSize,
            querySearch,
        }));
    }, [pageIndex, pageSize, sortDate, querySearch]);

    useEffect(() => {
        refreshTable.current = false;
    }, []);

    //Pagination info
    const firstEntry = (pageIndex * pageLimit) + 1;
    const lastEntry = (pageIndex * pageLimit) + pageLimit > totalEntries ? totalEntries : (pageIndex * pageLimit) + pageLimit;
    const pageInfo = totalEntries > 0 ? t("pagination-info", {firstEntry, lastEntry, totalEntries}) : t("no-results");

    return (
        <>

        <ScrollButton minScroll={600} condition={true} />

        {isLoading ? (<LoadingLocal/>): (
            <div className="table-responsive">
                <Table {...getTableProps()}>
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th
                                        className={` class-nowrap session-table-header ${column.id === "c4" ? "sortable-header" : ""} ${column.id === "registerid" ? "d-none" : ""}`}
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        onClick={column.id === "berichtsdatum" ? e => {
                                            setSortDate(!sortDate ? 1 : sortDate === 1 ? -1 : 0);
                                        } : undefined}
                                    >
                                        {column.render('Header')}
                                        {column.id === "berichtsdatum" && (
                                            <span>
                                                <b className={`${sortDate != 0 ? sortDate === -1 ? "caret-desc" : "caret-asc" : "caret-default"}`}></b>
                                            </span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, index) => {
                            prepareRow(row)
                                return (
                                    <React.Fragment key={`tbody-${index}`}>
                                        <tr {...row.getRowProps((state, rowInfo) => {
                                            if (rowInfo && rowInfo.row) {
                                                return {
                                                    style: {
                                                        fontWeight: 'normal',
                                                    }
                                                }
                                            } else return {}
                                        })}>
                                            {row.cells.map((cell, ci) => {
                                                return (
                                                    <td
                                                        {...cell.getCellProps({ key: `tr_${index}_${ci}` })}
                                                        className={`${cell.column.id === "c1" ? "d-flex" : ""} ${cell.column.id === "registerid" ? "d-none" : ""}`}
                                                    >
                                                        {cell.column.id === "c1" && <div className={`dot-${cell.value === "Unavailable" ? "un" : ''}available`}></div>}
                                                        {cell.render('Cell')}
                                                    </td>
                                                )
                                            })}
                                        </tr>

                                        <tr {...row.getRowProps({ key: `row_${index}_2` })}>
                                            {/* <pre> */}
                                            {
                                                row.cells.map((cell, index) => {
                                                    if (cell.column.id === "registerid") {

                                                        return (

                                                            <td {...cell.getCellProps({ key: `cell_${index}_2` })} colSpan={9} style={{ borderTop: "none" }}>
                                                            </td>
                                                        )
                                                    }
                                                })
                                            }
                                            {/* </pre> */}
                                        </tr>
                                    </React.Fragment>
                                )
                            })
                        }
                    </tbody>
                </Table>
            </div>
        )}
        

        {/* Pagination */}

        <Row className="text-center justify-content-center mt-3">
            <nav aria-label="Page navigation example">
                <Pagination
                    className="pagination justify-content-end"
                    listclassname="justify-content-end"
                >
                    <PaginationItem disabled={!canPreviousPage}>
                        <PaginationLink
                            href="#previous-page"
                            onClick={e => {
                                e.preventDefault()
                                goPreviousPage()
                            }}
                            tabIndex="-1"
                            className="bg-info text-white"
                        >
                            <i className='fa fa-angle-double-left'></i>
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={pageIndex < 2}>
                        <PaginationLink href="#minus-two" onClick={e => {
                            e.preventDefault();
                            goToPageNumber(pageIndex - 2);
                        }}>
                            {canPreviousPage && pageIndex > 1 ? pageIndex - 1 : " "}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={pageIndex === 0}>
                        <PaginationLink href="#minus-one"
                            onClick={e => {
                                e.preventDefault();
                                goPreviousPage()
                            }}>
                            {!canPreviousPage ? " " : pageIndex}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem active>
                        <PaginationLink href="#actual-page" onClick={e => { e.preventDefault() }} className="bg-info">
                            {pageIndex + 1}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!canNextPage}>
                        <PaginationLink href="#plus-one" onClick={e => {
                            e.preventDefault();
                            goNextPage();
                        }}>
                            {canNextPage ? pageIndex + 2 : " "}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!(pageCount - pageIndex > 2)}>
                        <PaginationLink href="#plus-two" onClick={e => {
                            e.preventDefault();
                            goToPageNumber(pageIndex + 2);
                        }}>
                            {(pageCount - pageIndex > 2) ? pageIndex + 3 : " "}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!canNextPage}>
                        <PaginationLink href="#next-page" onClick={e => {
                            e.preventDefault();
                            goNextPage();
                        }}
                            className="bg-info text-white"
                        >
                            <i className='fa fa-angle-double-right'></i>
                        </PaginationLink>
                    </PaginationItem>
                </Pagination>

                <Label>{pageInfo}</Label>
            </nav>
        </Row>
        
        </>
    )
}

export default AnalysisTableList;